import React, { useEffect, useState } from "react";

import ipaddr from "ipaddr.js";

import {
  Button,
  Checkbox,
  Dialog,
  FormSection,
  TextInput,
  Tooltip,
  IconButton,
  Row,
} from "@narmi/design_system";

import { useLocalization } from "@fluent/react";

import {
  createBankingToken,
  destroyBankingToken,
  getAllowedScopes,
  getClientIp,
  updateBankingToken,
} from "./api";

import { DeserializedBankingToken, DialogProps } from "./types";
import { getAlertError } from "./modals";

const isValidIP = (input: string) => {
  try {
    const [ip, mask] = input.split("/");
    const parsedIP = ipaddr.parse(ip);

    // If there's no CIDR mask, it's a valid standalone IP
    if (mask === undefined) return true;

    // Validate CIDR mask
    const maskNum = Number(mask);
    return parsedIP.kind() === "ipv4"
      ? maskNum >= 0 && maskNum <= 32
      : maskNum >= 0 && maskNum <= 128;
  } catch {
    return false;
  }
};

export const TokenDialogSubmitFooter = ({
  buttonText,
  onCancel,
  onConfirm,
}: {
  buttonText: string;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const { l10n } = useLocalization();
  return (
    <div style={{ display: "flex", justifyContent: "flex-end" }}>
      <Button style={{ paddingRight: "10px" }} kind="plain" onClick={onCancel}>
        {l10n.getString("button-cancel")}
      </Button>
      <Button onClick={onConfirm}>{buttonText}</Button>
    </div>
  );
};

const getScopeLabel = (scope: string, l10n: { getString: (id: string) => string }): string => {
  if (scope === "read") return l10n.getString("checkbox-label-read");
  if (scope === "write") return l10n.getString("checkbox-label-write");
  return scope;
};

const TokenWriteForm = ({
  nickname,
  onChangeNickname,
  ipAllowList,
  onChangeIpAllowList,
  scopes,
  onChangeScopes,
  allowedScopes,
}: {
  nickname: string;
  onChangeNickname: (val: string) => void;
  ipAllowList: string;
  onChangeIpAllowList: (val: string) => void;
  scopes: Set<string>;
  onChangeScopes: (val: Set<string>) => void;
  allowedScopes: string[];
}) => {
  const { l10n } = useLocalization();
  const handleScopeChange = (scope: string) => {
    scopes.has(scope) ? scopes.delete(scope) : scopes.add(scope);
    onChangeScopes(new Set(scopes));
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-around",
        gap: "12px",
        marginTop: "12px",
      }}
    >
      <TextInput
        label={l10n.getString("textinput-label-nickname")}
        value={nickname}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChangeNickname(e.target.value)
        }
      />
      <Row alignItems="center">
        <TextInput
          style={{ flexGrow: "1" }}
          label={l10n.getString("textinput-label-ip-allowlist")}
          value={ipAllowList}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChangeIpAllowList(e.target.value)
          }
          endContent={
            <Tooltip text={l10n.getString("tooltip-ip-allowlist-format")}>
              <span className="narmi-icon-info"></span>
            </Tooltip>
          }
        />
        <Tooltip text={l10n.getString("tooltip-add-current-ip")}>
          <IconButton
            name="zap"
            onClick={() => {
              getClientIp().then((ipResponse) =>
                onChangeIpAllowList(ipResponse.ip)
              );
            }}
          />
        </Tooltip>
      </Row>
      <span></span>
      <FormSection title={l10n.getString("form-section-scopes")} />
      {allowedScopes.map((scope) => (
        <Checkbox
          key={scope}
          label={getScopeLabel(scope, l10n)}
          kind="condensed"
          checked={scopes.has(scope)}
          onChange={() => handleScopeChange(scope)}
        />
      ))}
    </div>
  );
};

export const useTokenForm = ({
  onSubmit,
  updateToken,
}: {
  onSubmit: (data: {
    nickname: string;
    ip_allowlist: string[];
  }) => Promise<void> | void;
  updateToken?: DeserializedBankingToken;
}) => {
  const { l10n } = useLocalization();
  const initialNickName = updateToken?.nickname || "";
  const initialScopes: Set<string> = updateToken?.scopes || new Set();
  const initialIpAllowlist = updateToken?.ipAllowlist.join(", ") || "";

  const [nickname, setNickName] = useState(initialNickName);
  const [ipAllowList, setIpAllowList] = useState(initialIpAllowlist);
  const [scopes, setScopes] = useState(initialScopes);
  const [alertEl, setAlertEl] = useState<React.ReactNode>(null);
  const [allowedScopes, setAllowedScopes] = useState<string[]>([
    "read",
    "write",
  ]);

  useEffect(() => {
    getAllowedScopes().then((res) => setAllowedScopes(res.allowed_scopes));
  }, []);

  const clearForm = () => {
    setNickName("");
    setIpAllowList("");
    setScopes(new Set());
    setAlertEl(null);
  };

  const validate = () => {
    if (![nickname, ipAllowList, scopes.size].every(Boolean)) {
      setAlertEl(
        getAlertError(l10n.getString("alert-all-fields-required"), () =>
          setAlertEl(null)
        )
      );
      return false;
    }
    ipAllowList.split(",").forEach((ip) => {
      if (!isValidIP(ip.trim())) {
        setAlertEl(
          getAlertError(l10n.getString("alert-invalid-ip-address"), () =>
            setAlertEl(null)
          )
        );
      }
    });
    return true;
  };

  const handleConfirm = async () => {
    setAlertEl(null);
    if (!validate()) return;
    const data = {
      nickname,
      scope: Array.from(scopes),
      ip_allowlist: ipAllowList.split(",").map((ip) => ip.trim()),
    };
    await onSubmit(data);
  };

  const tokenForm = (
    <TokenWriteForm
      nickname={nickname}
      onChangeNickname={setNickName}
      ipAllowList={ipAllowList}
      onChangeIpAllowList={setIpAllowList}
      scopes={scopes}
      onChangeScopes={setScopes}
      allowedScopes={allowedScopes}
    />
  );

  return {
    tokenForm,
    alertEl,
    clearForm,
    handleConfirm,
  };
};

export const TokenCreateDialog = ({
  isOpen,
  onUserDismiss,
  onConfirm,
}: DialogProps) => {
  const { l10n } = useLocalization();
  const { tokenForm, alertEl, clearForm, handleConfirm } = useTokenForm({
    onSubmit: (data) => createBankingToken(data).then(onConfirm),
  });

  useEffect(() => {
    clearForm();
  }, [isOpen]);

  return (
    <Dialog
      title={l10n.getString("dialog-create-token-title")}
      isOpen={isOpen}
      onUserDismiss={onUserDismiss}
      footer={
        <TokenDialogSubmitFooter
          buttonText={l10n.getString("button-create-token")}
          onCancel={onUserDismiss}
          onConfirm={handleConfirm}
        />
      }
      notification={alertEl}
    >
      {tokenForm}
    </Dialog>
  );
};

export const TokenUpdateDialog = ({
  isOpen,
  onUserDismiss,
  onConfirm,
  tokenForUpdate,
}: DialogProps & { tokenForUpdate: DeserializedBankingToken | null }) => {
  const { l10n } = useLocalization();
  if (!tokenForUpdate) {
    return null;
  }

  const { tokenForm, alertEl, clearForm, handleConfirm } = useTokenForm({
    onSubmit: (data) =>
      updateBankingToken(tokenForUpdate.uuid, data).then(onConfirm),
    updateToken: tokenForUpdate,
  });

  useEffect(() => {
    if (!isOpen) clearForm();
  }, [isOpen]);

  return (
    <Dialog
      title={l10n.getString("dialog-edit-token-title")}
      isOpen={isOpen}
      onUserDismiss={onUserDismiss}
      footer={
        <TokenDialogSubmitFooter
          buttonText={l10n.getString("button-update-token")}
          onCancel={onUserDismiss}
          onConfirm={handleConfirm}
        />
      }
      notification={alertEl}
    >
      {tokenForm}
    </Dialog>
  );
};

export const TokenRevokeDialog = ({
  isOpen,
  onUserDismiss,
  onConfirm,
  tokenForRevoke,
}: DialogProps & { tokenForRevoke: DeserializedBankingToken | null }) => {
  const { l10n } = useLocalization();
  if (!tokenForRevoke) {
    return null;
  }

  return (
    <Dialog
      title={l10n.getString("dialog-revoke-token-title")}
      isOpen={isOpen}
      onUserDismiss={onUserDismiss}
      footer={
        <TokenDialogSubmitFooter
          buttonText={l10n.getString("button-revoke-token")}
          onCancel={onUserDismiss}
          onConfirm={() =>
            destroyBankingToken(tokenForRevoke.uuid).then(onConfirm)
          }
        />
      }
    >
      <p>{l10n.getString("dialog-revoke-token-message")}</p>
    </Dialog>
  );
};
